<template>
  <!-- Mobile: inline card (unchanged) -->
  <TCard v-if="shouldShow && isMobile" class="!p-0 flex-col">
    <OnboardingContent
      :adminStore="adminStore"
      :progress="progress"
      :onboardingCheckList="onboardingCheckList"
      :finishedState="finishedState"
      @run-tour="$emit('run-tour')"
      @skip="skip"
    />
  </TCard>

  <!-- Desktop: floating draggable window -->
  <Teleport to="body">
    <div
      v-if="shouldShow && !isMobile"
      ref="floatingEl"
      class="fixed z-[900] w-[380px] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden select-none"
      :style="floatingStyle"
    >
      <!-- Drag handle / header -->
      <div
        class="flex items-center justify-between px-4 py-3 bg-primary-50 cursor-grab active:cursor-grabbing border-b border-gray-100"
        @mousedown="startDrag"
      >
        <div class="flex items-center gap-2">
          <i class="pi pi-list-check text-primary-600" />
          <span class="font-semibold text-sm text-primary-700">רשימת משימות</span>
          <span class="text-xs text-primary-400 font-medium">{{ checkedCount }}/{{ onboardingCheckList.length }}</span>
        </div>
        <div class="flex items-center gap-1">
          <button
            class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-primary-100 transition-colors text-primary-600"
            @click="collapsed = !collapsed"
          >
            <i :class="['pi text-xs', collapsed ? 'pi-chevron-down' : 'pi-chevron-up']" />
          </button>
          <button
            class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-100 transition-colors text-gray-400 hover:text-red-500"
            @click="skip"
          >
            <i class="pi pi-times text-xs" />
          </button>
        </div>
      </div>

      <!-- Collapsible body -->
      <Transition name="collapse">
        <div v-show="!collapsed" class="overflow-hidden">
          <OnboardingContent
            :adminStore="adminStore"
            :progress="progress"
            :onboardingCheckList="onboardingCheckList"
            :finishedState="finishedState"
            :compact="true"
            @run-tour="$emit('run-tour')"
            @skip="skip"
          />
        </div>
      </Transition>

      <!-- Collapsed progress bar -->
      <div v-if="collapsed" class="px-4 py-2">
        <StepsProgressBar :progress="progress" :segments="onboardingCheckList.length" />
      </div>
    </div>
  </Teleport>
</template>

<!-- Inline sub-component for shared content -->
<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import StepsProgressBar from "@app/components/admin/components/StepsProgressBar.vue";
import { LottieAnimation } from "lottie-web-vue";
import confirmationJsonData from "../../../../../assets/animations/confirmation-v2.json";

interface ChecklistItem {
  id: string;
  action: string;
  description: string;
  icon: string;
  checked: boolean;
}

const OnboardingContent = defineComponent({
  name: 'OnboardingContent',
  components: { StepsProgressBar, LottieAnimation },
  props: {
    adminStore: { type: Object, required: true },
    progress: { type: Number, required: true },
    onboardingCheckList: { type: Array as PropType<ChecklistItem[]>, required: true },
    finishedState: { type: Boolean, required: true },
    compact: { type: Boolean, default: false },
  },
  emits: ['run-tour', 'skip'],
  setup() {
    return { confirmationJson: confirmationJsonData };
  },
  template: `
    <div>
      <div class="flex gap-2 justify-center items-start h-full flex-col p-4" :class="compact ? 'pb-2' : ''">
        <div class="flex justify-between w-full items-center">
          <span class="font-medium text-sm text-gray-400 self-start mt-2">מתחילים</span>
          <span
            class="p-1 px-2 flex gap-1 items-center border border-primary-400 rounded-full text-primary-700 hover:bg-primary-100 cursor-pointer"
            @click="$emit('run-tour')">
            <i class="pi pi-play-circle"/>
            <span class="text-xs">סיור באפליקציה</span>
          </span>
        </div>
        <span class="text-primary-600 font-bold" :class="compact ? 'text-base' : 'text-xl'">{{ adminStore.getBusinessSiteData.fullName }}, שנסיים את ההרשמה שלך? </span>
        <StepsProgressBar :progress="progress" :segments="onboardingCheckList.length" class="!my-4"/>
      </div>
      <div v-if="!finishedState" class="flex gap-2 justify-center items-start h-full flex-col">
        <div
          v-for="listItem of [onboardingCheckList[0], ...onboardingCheckList.slice(1).sort((a,b) => a.checked ? -1: 1 )]"
          :key="listItem.id"
          class="flex p-4 border-b border-gray-100 w-full flex-col"
          :class="compact ? 'py-3 px-4' : ''"
        >
          <div class="flex gap-2 w-full items-center">
            <i :class="['pi', listItem.icon, listItem.checked ? 'text-gray-500' : '']"/>
            <span :class="[{'line-through text-gray-400': listItem.checked}, compact ? 'text-sm' : '']">{{ listItem.action }}</span>
            <div
              class="flex items-center justify-center p-[2px] rounded-full w-5 h-5 mr-auto ml-1"
              :class="[listItem.checked ? 'bg-[#ffab1a]' : 'bg-gray-200']"
            >
              <i v-if="listItem.checked" class="pi pi-check text-white" style="font-size: 0.6rem"/>
            </div>
          </div>
          <span class="text-xs font-medium text-gray-500 mt-1" :class="{'line-through text-gray-400': listItem.checked}">{{
              listItem.description
            }}</span>
        </div>
      </div>
      <div v-else class="w-full flex flex-col items-center justify-center pb-4 gap-4">
        <LottieAnimation
          ref="anim"
          class="w-2/3"
          :animationData="confirmationJson"
          :autoPlay="true"
          :loop="false"
          :speed="1"/>
        <span class="font-medium">איזה כיף, אתם מוכנים להתחיל!</span>
      </div>
      <section v-if="!compact" class="text-primary-400 w-full font-semibold text-center py-2 cursor-pointer" @click="$emit('skip')">
        דילוג
      </section>
    </div>
  `,
});

export { OnboardingContent };
</script>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useAdminAvailabilityStore, useAdminStore} from "@app/stores";
import {IWorkingHoursItem} from "@torim-app/types";
import {useAdminClientsStore} from "@app/stores/admin-clients";
import {useStorage} from "@app/composables/use-storage";
import {AdminApi} from "@app/api/admin/admin-api";
import { trackEvent } from '@app/composables/posthog';
import { useResponsive } from '@app/composables/responsive';

defineEmits(['run-tour'])

const adminStore = useAdminStore();
const adminAvailabilityStore = useAdminAvailabilityStore();
const adminClientsStore = useAdminClientsStore();
const {getValue, setValue} = useStorage();
const { isMobile } = useResponsive();
const finishedState = ref<boolean>(false)
const collapsed = ref(false);

const onboardingCheckList = ref<{ id: string; action: string; description: string; icon: string; checked: boolean }[]>([
  {
    id: 'new-business',
    action: 'רישום עסק',
    description: 'רישום העסק שלכם והפרטים הבסיסיים עליכם.',
    icon: 'pi-sparkles',
    checked: true
  },
  {
    id: 'services',
    action: 'שירותים ושעות פעילות',
    description: 'תפריט ← הגדרות העסק ← הוספת שירותים ושעות פעילות.',
    icon: 'pi-id-card',
    checked: false
  }, {
    id: 'first-setup',
    action: 'הגדרה ראשונית',
    description: 'תפריט ← הגדרות. ההמלצה שלנו היא להתחיל עם הגדרות זימון תורים ואתר הנחיתה. התאימו את האפליקציה לצורת העבודה שלכם.',
    icon: 'pi-cog',
    checked: false
  }, {
    id: 'share',
    action: 'שיתוף עמוד הנחיתה שלכם',
    description: 'לחצו על כפתור ״עמוד הנחיתה״ פה למטה.',
    icon: 'pi-share-alt',
    checked: false
  }, {
    id: 'customers',
    action: 'הוספת לקוחות',
    description: 'תפריט ← ניהול לקוחות ← יצירת לקוח או ייבוא מאנשי הקשר.',
    icon: 'pi-users',
    checked: false
  }
])

const checkedCount = computed(() => onboardingCheckList.value.filter(x => x.checked).length);

const progress = computed<number>(() => {
  return (onboardingCheckList.value.filter(x => x.checked).length / onboardingCheckList.value.length) * 100
})
const shouldShow = computed<boolean>(() => {
  return !adminStore.getSavedStates?.isFinishedOnBoarding;
})

// ── Draggable floating window (desktop) ──
const floatingEl = ref<HTMLElement | null>(null);
const posX = ref(24);
const posY = ref(80);
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

const floatingStyle = computed(() => ({
  left: `${posX.value}px`,
  top: `${posY.value}px`,
}));

const startDrag = (e: MouseEvent) => {
  if (!floatingEl.value) return;
  isDragging.value = true;
  dragOffset.value = {
    x: e.clientX - posX.value,
    y: e.clientY - posY.value,
  };
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = (e: MouseEvent) => {
  if (!isDragging.value || !floatingEl.value) return;
  const rect = floatingEl.value.getBoundingClientRect();
  const newX = e.clientX - dragOffset.value.x;
  const newY = e.clientY - dragOffset.value.y;
  // Clamp to viewport
  posX.value = Math.max(0, Math.min(newX, window.innerWidth - rect.width));
  posY.value = Math.max(0, Math.min(newY, window.innerHeight - rect.height));
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
});

// ── Checklist logic ──
const getOnboardingChecklist = async (): Promise<Array<string>> => {
  const res = await getValue('onboarding-checklist');
  if (res)
    return JSON.parse(res) as Array<string>;

  return []
}

const complete = () => {
  trackEvent('onboarding_completed');
  finishedState.value = true;
  setTimeout(() => {
    AdminApi.updateState({isFinishedOnBoarding: true})
      .then(() => adminStore.fetchBusinessData())
      .catch(x => console.error(x))
  }, 3000);
}

const setOnboardingChecklistItem = async (id: string, forceIn = false) => {
  let items = await getOnboardingChecklist();
  if (!items.includes(id))
    items.push(id);
  else if (!forceIn)
    items = items.filter(x => x !== id)

  await setValue('onboarding-checklist', JSON.stringify(items))

  const itemToUpdate = onboardingCheckList.value.find(x => x.id === id);
  if (itemToUpdate)
    itemToUpdate.checked = forceIn ? true : !itemToUpdate.checked;

  // All is finished
  if (items.length === onboardingCheckList.value.length) {
    complete();
  }
}

const skip = async () => {
  trackEvent('onboarding_skipped');
  AdminApi.updateState({isFinishedOnBoarding: true})
    .then(() => adminStore.fetchBusinessData())
    .catch(x => console.error(x))
}

watch([
  () => adminAvailabilityStore?.getAvailability,
  () => adminStore?.getServices,
  () => adminStore?.getSavedStates,
  () => adminClientsStore?.getClients
], () => {
  if (adminAvailabilityStore?.getAvailability && adminStore?.getServices) {
    const allAvailabilities = Object.values(adminAvailabilityStore?.getAvailability[adminStore.getUserId] ?? {})?.map<IWorkingHoursItem[]>(x => Object.values(x))?.flat()
    if (allAvailabilities.length > 0 && adminStore.getServices.length > 0)
      setOnboardingChecklistItem('services', true)
  }

  if (adminClientsStore.getClients) {
    if (adminClientsStore.getClients.length > 0)
      setOnboardingChecklistItem('customers', true)
  }

  if (adminStore.getSavedStates) {
    if (adminStore.getSavedStates.isSharedLandingPage)
      setOnboardingChecklistItem('share', true)
    if (adminStore.getSavedStates.isEditedSettings)
      setOnboardingChecklistItem('first-setup', true)
  }
}, {deep: true})

watch(progress, () => {
  if (progress.value === 100)
    complete();
})

onMounted(() => {
  getOnboardingChecklist().then(res => {
    let i = 1;
    for (const key of res) {
      const idx = onboardingCheckList.value.findIndex(x => x.id === key);
      if (idx)
        setTimeout(() => {
          onboardingCheckList.value[idx].checked = true;
          i++;
        }, 450 * i)
    }
  });
})
</script>

<style scoped lang="scss">
.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  max-height: 600px;
  overflow: hidden;
}
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
